<template>
  <va-form
    class="create-author__form"
    ref="form"
    v-model="form"
    :before-save="beforeSave"
    :after-save="handleAfterSave"
    :required-fields-set="requiredFieldsSet"
    @finishedSaving="finishedSaving">
    <!-- Phone number card --->
    <div
      v-for="(_, index) in phoneNumbers"
      :key="index"
      :class="{ 'spacing-bottom--24': index !== phoneNumbers.length - 1 }">
      <v-card class="spacing-bottom--24">
        <v-card-title class="spacing-bottom--16">
          <span>{{ $t("resources.phone_numbers.create.phone") }} {{ index + 1 }}</span>
          <v-icon v-if="index !== 0" class="ml-auto" :size="18" color="error" @click="removePhoneNumber(index)">
            mdi-trash-can-outline</v-icon
          >
        </v-card-title>
        <va-autocomplete-input
          class="spacing-bottom--16"
          required
          v-model="phoneNumbers[index].domain"
          :label="$t('resources.phone_numbers.create.domain')"
          :placeholder="$t('resources.phone_numbers.create.domain_placeholder')"
          :choices="domainsList"
          :min-chars="1"
          validate-on-blur
          cache-items></va-autocomplete-input>
        <va-text-input
          v-model="phoneNumbers[index].publisherName"
          required
          class="spacing-bottom--16"
          :label="$t('resources.phone_numbers.create.publisher_name')"
          :placeholder="$t('resources.phone_numbers.create.publisher_name_placeholder')"></va-text-input>
        <va-text-input
          v-model="phoneNumbers[index].publisherSubId"
          required
          class="spacing-bottom--16"
          :label="$t('resources.phone_numbers.create.publisher_sub_id')"
          :placeholder="$t('resources.phone_numbers.create.publisher_sub_id_placeholder')"></va-text-input>
        <va-text-input
          v-model="phoneNumbers[index].campaignId"
          required
          class="spacing-bottom--16"
          :label="$t('resources.phone_numbers.create.campaign_id')"
          :placeholder="$t('resources.phone_numbers.create.campaign_id_placeholder')"></va-text-input>
        <va-text-input
          v-model="phoneNumbers[index].numberPoolId"
          required
          class="spacing-bottom--16"
          :label="$t('resources.phone_numbers.create.number_pool_id')"
          :placeholder="$t('resources.phone_numbers.create.number_pool_id_placeholder')"></va-text-input>
        <v-btn-toggle class="va-btn-toggle" v-model="phoneNumbers[index].tollFree" mandatory tile color="primary" group>
          <v-btn :value="false">
            {{ $t("resources.phone_numbers.create.number_type_local") }}
          </v-btn>
          <v-btn :value="true">
            {{ $t("resources.phone_numbers.create.number_type_toll_free") }}
          </v-btn>
        </v-btn-toggle>
        <va-text-input
          v-model="phoneNumbers[index].tollFreeAreaCode"
          class="spacing-top--16"
          :label="$t('resources.phone_numbers.create.area_code')"
          :placeholder="$t('resources.phone_numbers.create.area_code_placeholder')"
          :rules="getAreaCodeRules(phoneNumbers[index].tollFree)"></va-text-input>
      </v-card>
    </div>
    <!-- Phone number card END --->

    <v-card>
      <v-card-title class="spacing-bottom--16">{{
        $t("resources.phone_numbers.create.add_phone_number")
      }}</v-card-title>

      <v-btn elevation="0" class="add-phone-number-btn" @click="addPhoneNumber">
        <v-icon class="mr-1" size="16">mdi-plus</v-icon>
        {{ $t("resources.phone_numbers.create.add_phone_number") }}
      </v-btn>
    </v-card>
  </va-form>
</template>

<script>
  // Components
  // Mixins
  import crudFormMixin from "@/mixins/crudFormMixin";
  import ConnectedDomainsList from "@/mixins/connectedDomainsList";
  // Constants
  import { PHONE_NUMBER } from "@/constant";
  // Helpers
  import cloneDeep from "lodash/cloneDeep";

  export default {
    name: "PhoneNumberForm",
    mixins: [crudFormMixin, ConnectedDomainsList],
    data() {
      return {
        requiredFormFields: [],
        form: {},
        phoneNumbers: [cloneDeep(PHONE_NUMBER)],
      };
    },
    beforeMount() {
      this.form.items = this.phoneNumbers;
      this.formOriginalState = JSON.stringify(this.form);
    },
    methods: {
      getAreaCodeRules(isTollFree) {
        const rules = [
          (v) => !v || /^\d+$/.test(v) || this.$t("resources.phone_numbers.create.area_code_numeric"),
          (v) => !v || v.length === 3 || this.$t("resources.phone_numbers.create.area_code_length"),
          (v) => !v || (Number(v) >= 800 && Number(v) <= 900) || this.$t("resources.phone_numbers.create.area_code_range"),
        ];

        if (isTollFree) {
          rules.unshift((v) => !!v || this.$t("resources.phone_numbers.create.area_code_required"));
        }

        return rules;
      },
      handleAfterSave(data) {
        if (data?.message) {
          this.$admin.toast.info(data?.message);
        }

        this.afterSave();
      },
      async removePhoneNumber(index) {
        if (
          await this.$admin.confirm(
            this.$t("resources.phone_numbers.create.delete_phone_number"),
            "",
            this.$t("overrides.confirm.delete_message"),
          )
        ) {
          this.phoneNumbers.splice(index, 1);
        }
      },
      finishedSaving() {
        this.$emit("finishedSaving");
      },
      addPhoneNumber() {
        const newPhoneNumber = {
          ...PHONE_NUMBER,
          domain: this.phoneNumbers[0].domain,
          campaignId: this.phoneNumbers[0].campaignId,
          numberPoolId: this.phoneNumbers[0].numberPoolId,
        };

        this.phoneNumbers.push(cloneDeep(newPhoneNumber));
      },
      submit() {
        this.setSubmitAttempted();
        this.$refs.form.onSubmit();
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import "../../@core/preset/preset/functions.scss";
  @import "../../@core/preset/preset/typography/index.scss";

  .va-btn-toggle {
    border-radius: 4px;

    .v-btn {
      margin: 0;
      box-shadow: none !important;
      border: 1px solid get-color("secondary-btn");
      color: get-color("secondary-btn");
    }
  }

  .add-phone-number-btn {
    border-radius: 6px;
    padding: 8px;
    background-color: get-color("primary-10-hover") !important;
    border: 1px dashed get-color("primary-65") !important;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    color: get-color("secondary-btn") !important;
    @include text-button-medium();
  }
</style>
